import { ArrowTopRightOnSquareIcon } from "@heroicons/react/24/outline";
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import {
  ColumnOneTableCellSkeleton,
  SecondaryButton,
  Table,
  TableCellSkeleton,
} from "../../../components";
import { columnHelper } from "../../../components/shared/SharedSessionColumns";
import { useDialogContext } from "../../../contexts/DialogContext";
import { useUIContext } from "../../../contexts/UIContext";
import { useAppDispatch, useAppSelector } from "../../../redux/hooks";
import { customAnalysisFormSlice } from "../../../redux/slices/customAnalysisFormSlice";
import {
  useGetRequestedAnalysesQuery,
  useGetRequestedAnalysisByIdQuery,
} from "../../../services";
import {
  convertUTCToLocalTime,
  joinClassNames,
} from "../../../shared/Functions";
import { MocapType } from "../../../types/sessionTypes";

export const CustomReports: React.FC = (): JSX.Element => {
  // State and AppContext
  const { setDisplayBack } = useUIContext();
  const { setDialogState } = useDialogContext();
  const [selectedPageSize, setSelectedPageSize] = useState(
    sessionStorage.getItem("processedSessionPageSize") || "10",
  );
  const [analysisId, setAnalysisId] = useState<number>(0);

  // RTK Queries
  const { data: requestedAnalyses, isFetching } =
    useGetRequestedAnalysesQuery();
  const { data: singleAnalysis } = useGetRequestedAnalysisByIdQuery(
    analysisId,
    {
      skip: analysisId === 0,
    },
  );

  // RTK slice actions
  const dispatch = useAppDispatch();
  const { storeCustomAnalysisFormState } = customAnalysisFormSlice.actions;

  // Redux calls
  const customAnalysisFormState = useAppSelector(
    (state) => state.customAnalysisForm,
  );
  const mocapTypes = useAppSelector((state) => state.org.details.mocapTypes);
  const playerList = useAppSelector((state) => state.players.playerList);

  const navigate = useNavigate();

  const determineCriteriaForSelectedAnalysis = (segmentCriteria: any) => {
    const criteria: any[] = [];

    const addCriteria = (
      field: string,
      label: string,
      value: any,
      comparison: { value: string; label: string },
      optionLabel?: string,
    ) => {
      criteria.push({
        comparison,
        field: { value: field, label },
        option: optionLabel ? { value, label: optionLabel } : value,
      });
    };

    if (segmentCriteria.mocapTypeId) {
      const mocapType = mocapTypes.find(
        ({ id }: MocapType) => id === segmentCriteria.mocapTypeId,
      );
      mocapType &&
        addCriteria(
          "mocapType",
          "Mocap Type",
          {
            value: mocapType.slug,
            label: mocapType.name,
            id: mocapType.id,
          },
          { value: "is", label: "is" },
        );
    }

    const fieldMappings = [
      {
        field: "horizontalBreak",
        label: "Horizontal Break",
        key: "horizontalBreak",
        unit: "in",
      },
      {
        field: "verticalBreak",
        label: "Vertical Break",
        key: "verticalBreak",
        unit: "in",
      },
      {
        field: "inducedVerticalBreak",
        label: "Induced Vertical Break",
        key: "inducedVerticalBreak",
        unit: "in",
      },
      {
        field: "releaseSpeed",
        label: "Release Speed",
        key: "relSpeedMph",
        unit: "mph",
      },
    ];

    fieldMappings.forEach(({ field, label, key, unit }) => {
      if (segmentCriteria[key]) {
        const value = segmentCriteria[key][0];
        const comparisonMapping: Record<
          string,
          { value: string; label: string }
        > = {
          "<": { value: "lessThan", label: "<" },
          ">": { value: "greaterThan", label: ">" },
          "<=": { value: "lessThanOrEqual", label: "<=" },
          ">=": { value: "greaterThanOrEqual", label: ">=" },
        };

        const operator = Object.keys(comparisonMapping)
          .sort((a, b) => b.length - a.length)
          .find((op) => value.startsWith(op));

        if (operator) {
          addCriteria(
            field,
            label,
            parseFloat(value.slice(operator.length)),
            comparisonMapping[operator],
            unit,
          );
        }
      }
    });

    const multiSelectMappings = [
      {
        field: "player",
        label: "Player",
        ids: segmentCriteria.orgPlayerIds || [],
        list: playerList,
        key: "orgPlayerId",
        format: (player: any) => ({
          value: `${player.firstName} ${player.lastName}`,
          label: `${player.firstName} ${player.lastName}`,
          id: player.orgPlayerId,
        }),
      },
      {
        field: "pitchType",
        label: "Pitch Type",
        ids: segmentCriteria.pitchTypes || [],
        format: (pitchType: string) => ({ value: pitchType, label: pitchType }),
      },
      {
        field: "mocapPitchType",
        label: "Mocap Pitch Type",
        ids: segmentCriteria.mocapPitchTypes || [],
        format: (mocapPitchType: string) => ({
          value: mocapPitchType,
          label: mocapPitchType,
        }),
      },
    ];

    multiSelectMappings.forEach(({ field, label, ids, list, key, format }) => {
      const options =
        list && key
          ? ids
              .map((id: any) => list.find((item: any) => item[key] === id))
              .filter(Boolean)
              .map(format)
          : ids.map(format);

      if (options.length) {
        addCriteria(field, label, options, {
          value: "includes",
          label: "includes",
        });
      }
    });

    return criteria;
  };

  useEffect(() => {
    if (singleAnalysis) {
      const mocapType = mocapTypes.find((type: MocapType) => {
        return type.id === singleAnalysis.mocapTypeId;
      });

      const analysisToEdit = {
        ...customAnalysisFormState,
        analysisId: singleAnalysis.id,
        name: singleAnalysis.name,
        hand: singleAnalysis.domHand,
        movementType: singleAnalysis.movementType,
        mocapType: mocapType
          ? {
              value: mocapType.slug,
              label: mocapType.name,
              id: mocapType.id,
            }
          : null,
        primarySegment: {
          dateCriteria: singleAnalysis.primarySegment.dateCriteria,
          dateRange: singleAnalysis.primarySegment.dateRange,
          criteria: determineCriteriaForSelectedAnalysis(
            singleAnalysis.primarySegment.criteria,
          ),
          id: singleAnalysis.primarySegment.id,
        },
      };

      if (singleAnalysis.populationSegment) {
        Object.assign(analysisToEdit, {
          populationSegment: {
            dateCriteria: singleAnalysis.populationSegment.dateCriteria,
            dateRange: singleAnalysis.populationSegment.dateRange,
            criteria: determineCriteriaForSelectedAnalysis(
              singleAnalysis.populationSegment.criteria,
            ),
            id: singleAnalysis.populationSegment.id,
          },
        });
      } else if (singleAnalysis.comparisonSegment) {
        Object.assign(analysisToEdit, {
          comparisonSegment: {
            dateCriteria: singleAnalysis.comparisonSegment.dateCriteria,
            dateRange: singleAnalysis.comparisonSegment.dateRange,
            criteria: determineCriteriaForSelectedAnalysis(
              singleAnalysis.comparisonSegment.criteria,
            ),
            id: singleAnalysis.comparisonSegment.id,
          },
        });
      }
      dispatch(storeCustomAnalysisFormState(analysisToEdit));
      navigate("/request-analysis");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [singleAnalysis]);

  const parseAnalysisData = (data: any) => {
    return data.map((analysis: any) => {
      const getAnalysisStatus = () => {
        if (analysis.status === "completed") {
          return "Complete";
        } else if (analysis.status === "in_progress") {
          return "In-progress";
        } else if (analysis.status === "draft") {
          return "Draft";
        } else if (analysis.status === "requested") {
          return "Requested";
        } else if (analysis.status === "failed") {
          return "Failed";
        } else {
          return "";
        }
      };
      const getActions = () => {
        if (analysis.status === "completed") {
          return "Analysis";
        } else if (analysis.status === "draft") {
          return "Edit";
        } else {
          return "";
        }
      };
      return {
        ...analysis,
        status: getAnalysisStatus(),
        actions: getActions(),
      };
    });
  };

  const customReportsTableData = parseAnalysisData(requestedAnalyses || []);

  const customReportsColumns = useMemo(
    () => [
      columnHelper.accessor("name", {
        header: "Analysis Name",
        enableSorting: false,
        size: 600,
        maxSize: 800,
        cell: function nameCell(element: any) {
          return (
            <p
              className="font-medium text-gray-900 cursor-default"
              data-testid="custom-analysis-name"
            >
              {element.getValue()}
            </p>
          );
        },
      }),
      columnHelper.accessor("createdAt", {
        header: "Date Created",
        sortingFn: "datetime",
        cell: function dateCell(element: any) {
          return (
            <div
              className="font-medium text-gray-900 cursor-default"
              data-testid="custom-analysis-date-created"
            >
              {convertUTCToLocalTime(element.getValue())}
            </div>
          );
        },
      }),
      columnHelper.accessor("status", {
        header: "Status",
        enableSorting: false,
        cell: function statusCell(element: any) {
          return (
            <p
              className={joinClassNames(
                element.getValue() === "Complete" ? "text-green-600" : "",
                "cursor-default",
              )}
              data-testid="custom-analysis-status"
            >
              {element.getValue()}
            </p>
          );
        },
      }),
      columnHelper.accessor("actions", {
        header: "",
        enableSorting: false,
        cell: function actionsCell(element: any) {
          const url: string = element.row.original.reportUrl;
          const id = element.row.original.id;
          return (
            <div
              className="flex justify-end pr-8"
              data-testid="custom-analysis-actions-container"
            >
              {element.getValue() === "Analysis" && url && (
                <>
                  <a
                    href={url}
                    target="_blank"
                    rel="noreferrer"
                    data-testid="custom-analysis-link"
                  >
                    <SecondaryButton data-testid="view-custom-analysis-button">
                      View
                    </SecondaryButton>
                  </a>
                  <SecondaryButton
                    data-testid="re-request-custom-analysis-button"
                    className="ml-4"
                    onClick={() => setAnalysisId(id)}
                  >
                    Request Again
                  </SecondaryButton>
                </>
              )}
            </div>
          );
        },
      }),
    ],
    [],
  );

  const tableData = useMemo(
    () => (isFetching ? Array(10).fill({}) : customReportsTableData),
    [isFetching, customReportsTableData],
  );

  const tableColumns = useMemo(
    () =>
      isFetching
        ? customReportsColumns.map((column, index) => ({
            ...column,
            cell:
              index === 0 ? (
                <ColumnOneTableCellSkeleton />
              ) : (
                <TableCellSkeleton />
              ),
          }))
        : customReportsColumns,
    [isFetching, customReportsColumns],
  );

  useEffect(() => {
    setDisplayBack && setDisplayBack(false);
  }, [setDisplayBack]);

  const buttons = [
    {
      type: "primary",
      text: "Build New Analysis",
      action: () => setDialogState({ shouldOpen: true, type: "analysis" }),
      dataTestid: "create-analysis-button",
    },
    {
      type: "secondary",
      text: "Advanced",
      action: () => {
        const newWindow = window.open(
          "https://colab.research.google.com/github/RebootMotion/reboot-toolkit/blob/main/examples_api/RebootMotionCustomReport.ipynb",
          "_blank",
          "noopener,noreferrer",
        );
        if (newWindow) newWindow.opener = null;
      },
      dataTestid: "advanced-analysis-button",
      Icon: ArrowTopRightOnSquareIcon,
    },
  ];

  const tableFilters = useMemo(
    () => [
      {
        type: "dropdown",
        columnId: "status",
        placeholder: "All Analyses",
        filterOptions: [
          { value: "", label: "All Analyses" },
          { value: "requested", label: "Requested" },
          { value: "complete", label: "Complete" },
        ],
      },
    ],
    [],
  );

  return (
    <main
      className="relative pb-20 z-0 overflow-y-auto w-full h-full mt-8"
      data-testid="custom-reports-page"
    >
      <div data-testid="custom-analysis-table-container">
        <Table
          columns={tableColumns}
          data={tableData}
          data-testid="custom-analysis-table"
          selectedPageSize={selectedPageSize}
          setSelectedPageSize={setSelectedPageSize}
          displayFooter={true}
          fetching={isFetching}
          customRowClassName="custom-analysis-table-row"
          component="customAnalysis"
          buttons={buttons}
          filters={tableFilters}
        />
      </div>
    </main>
  );
};
